export interface FormSection {
  id?: number;
  title: string;
  fields?: FormField[];
}

export interface FormField {
  name: string;
  label: string;
  required?: boolean;
  const?: any;
  form_element?: string;
  input_type?: string;
  placeholder?: string;
  type?: string;
  enum?: Array<{ key: any; display: string; disabled?: boolean }>;
  prepared_destination_path?: string;
}

export interface ParseMissingFieldsResult {
  fields: string[];
  errorDetails: Record<string, string>;
}

// NEEDS TO BE IMPROVED ON. Temporary placeholder of a helper function to parse missing fields from test connection errors
export const parseMissingFields = (
  errorMessage: string,
  filteredFormSections: FormSection[],
): ParseMissingFieldsResult => {
  // Extract field names from error message like: "missing required field(s): 'project_id', 'schema', 'bucket_gcs'"
  // OR "missing one of: gcp_service_account_role, federated_gcp_service_account_role, gcp_service_account_key"
  // OR "error at /destination/bigquery/gcp_service_account_role/service_account_email: does not match pattern"
  const requiredFieldsMatch = errorMessage.match(
    /missing required field\(s\): '([^']+)'(?:, '([^']+)')*(?:, '([^']+)')*/,
  );
  const oneOfMatch = errorMessage.match(/missing one of: ([^,]+)(?:, ([^,]+))*(?:, ([^,]+))*/);
  const validationErrorMatch = errorMessage.match(
    /error at \/destination\/[^\/]+\/([^\/]+)(?:\/([^\/]+))*(?:\/([^\/]+))*/,
  );

  let extractedFields: string[] = [];
  let errorDetails: Record<string, string> = {};

  if (requiredFieldsMatch) {
    // Extract all captured groups and filter out undefined values
    extractedFields = requiredFieldsMatch.slice(1).filter(Boolean);
    extractedFields.forEach((field) => {
      errorDetails[field] = 'Field is required';
    });
  } else if (oneOfMatch) {
    // Extract all captured groups and filter out undefined values, then trim whitespace
    extractedFields = oneOfMatch
      .slice(1)
      .filter(Boolean)
      .map((field) => field.trim());
    extractedFields.forEach((field) => {
      errorDetails[field] = 'One of these authentication methods is required';
    });
  } else if (validationErrorMatch) {
    // Extract field name from validation error path - everything between last / and :
    const fieldNameMatch = errorMessage.match(
      /error at \/destination\/[^:]+: does not match pattern/,
    );
    if (fieldNameMatch) {
      // Extract the full path and get the last part
      const fullPathMatch = errorMessage.match(
        /error at (\/destination\/[^:]+): does not match pattern/,
      );
      if (fullPathMatch) {
        const fullPath = fullPathMatch[1];
        const pathParts = fullPath.split('/');
        const fieldName = pathParts[pathParts.length - 1]; // Get the last part
        extractedFields = [fieldName];

        // Extract the specific validation error message
        const validationMessageMatch = errorMessage.match(/does not match pattern '([^']+)'/);
        if (validationMessageMatch) {
          const pattern = validationMessageMatch[1];
          errorDetails[fieldName] = `does not match pattern '${pattern}'`;
        } else {
          errorDetails[fieldName] = 'Invalid format';
        }
      }
    }
  }

  if (extractedFields.length > 0) {
    // Build dynamic field name mapping from form sections
    const fieldNameMapping: Record<string, string> = {};

    // Add destination name field
    fieldNameMapping['name'] = 'name';

    // Build mapping from prepared_destination_path to field names
    filteredFormSections.forEach((section) => {
      section.fields?.forEach((field) => {
        // Map the field name to itself (for direct matches)
        fieldNameMapping[field.name] = field.name;

        // Handle special mappings based on prepared_destination_path
        if (field.prepared_destination_path) {
          const pathParts = field.prepared_destination_path.split('.');
          if (pathParts.length > 1) {
            // Map the last part of the path to the field name
            const pathFieldName = pathParts[pathParts.length - 1];
            fieldNameMapping[pathFieldName] = field.name;

            // For auth method related fields, map the auth method name to the required field
            // e.g., gcp_service_account_role.service_account_email -> map gcp_service_account_role to service_account_email
            if (pathParts.length > 2 && pathParts[1] && pathParts[2]) {
              const authMethod = pathParts[1];
              const requiredField = pathParts[2];
              fieldNameMapping[authMethod] = field.name;
            }
          }
        }
      });
    });

    // Map the extracted fields to their actual form field names
    const mappedFields = extractedFields.map((field) => fieldNameMapping[field] || field);

    // Map the error details to the mapped field names
    const mappedErrorDetails: Record<string, string> = {};
    extractedFields.forEach((originalField, index) => {
      const mappedField = mappedFields[index];
      mappedErrorDetails[mappedField] = errorDetails[originalField];
    });

    return { fields: mappedFields, errorDetails: mappedErrorDetails };
  }
  return { fields: [], errorDetails: {} };
};
